/**
 * Team Management (Owner-only).
 *
 * Simple, focused surface for the owner to manage their people:
 *   • Invite a team member       — POST   /team
 *   • Resend an invitation email — POST   /team/{id}/resend-invite
 *   • Edit display fields        — PATCH  /team/{id}   (name, job_title, department)
 *   • Remove a team member       — DELETE /team/{id}   (with confirmation)
 *   • Bulk reminders             — POST   /notifications/reminders
 *
 * Vocabulary note: the field the owner sees is "Job title" (Registered
 * Pharmacist, Dispensary Lead, etc.). Backend calls it `job_title` on
 * the wire; DB still stores it in `team_members.role` for backward
 * compatibility — see auth architecture review, phase-1 rec 3.
 */

import { useEffect, useState } from "react";
import {
  UserPlus,
  Mail,
  BadgeCheck,
  X,
  Send,
  ShieldCheck,
  Pencil,
  Trash2,
  Loader2,
  AlertTriangle,
  Search,
  Link2,
} from "lucide-react";
import AppShell from "../AppShell";
import { api } from "../api";
import { useAuth } from "../AuthContext";
import {
  PageHeader,
  Button,
  ErrorBanner,
  SuccessBanner,
  EmptyState,
} from "../../components/primitives";

export default function Team() {
  const { business } = useAuth();
  const [members, setMembers] = useState([]);
  const [compByMember, setCompByMember] = useState({});
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | active | pending
  const isDemo = business?.is_demo;

  async function load() {
    try {
      const [data, comp] = await Promise.all([
        api.get("/team"),
        api.get("/competencies/assignments").catch(() => []),
      ]);
      setMembers(data);
      const map = {};
      for (const c of comp) {
        const b = (map[c.team_member_id] ||= { assigned: 0, completed: 0 });
        b.assigned += 1;
        if (c.status === "completed") b.completed += 1;
      }
      setCompByMember(map);
    } catch (e) {
      setError(e.message);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function resendInvite(memberId) {
    setNotice("");
    setError("");
    try {
      const res = await api.post(`/team/${memberId}/resend-invite`);
      setNotice(res.invite_sent ? "Invite email resent." : "Resent — but the email could not be delivered.");
      await load();
    } catch (e) {
      setError(e.message);
    }
  }

  async function copyInviteLink(memberId) {
    setNotice("");
    setError("");
    try {
      const res = await api.post(`/team/${memberId}/resend-invite`);
      const link = res.invite_link;
      if (!link) {
        setError("No invite link available (member may already be active).");
        return;
      }
      try {
        await navigator.clipboard.writeText(link);
        setNotice("Invite link copied to clipboard — paste it anywhere (WhatsApp, SMS, Slack).");
      } catch {
        // Clipboard blocked (iOS Safari in some contexts); fall back to
        // a manual prompt so the owner can still grab the URL.
        window.prompt("Copy this invite link:", link);
        setNotice("Invite link ready — copy it from the dialog above.");
      }
      await load();
    } catch (e) {
      setError(e.message);
    }
  }

  async function sendReminders() {
    setNotice("");
    setError("");
    try {
      const res = await api.post("/notifications/reminders");
      setNotice(`Reminders dispatched to ${res.reminded} of ${res.candidates} staff with outstanding items.`);
    } catch (e) {
      setError(e.message);
    }
  }

  const filtered = members.filter((m) => {
    if (filter === "active" && m.invite_status !== "active") return false;
    if (filter === "pending" && m.invite_status !== "pending") return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const hay = `${m.name || ""} ${m.email || ""} ${m.job_title || m.role || ""} ${m.department || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const counts = {
    all: members.length,
    active: members.filter((m) => m.invite_status === "active").length,
    pending: members.filter((m) => m.invite_status === "pending").length,
  };

  return (
    <AppShell>
      <div className="flex items-start justify-between gap-5 mb-8" data-testid="team-page">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500 font-semibold">
            Team management
          </div>
          <h1 className="mt-2 font-display text-3xl tracking-tight text-slate-900 font-semibold">
            Your people
          </h1>
          <p className="mt-2 text-slate-500 text-[14.5px] max-w-xl">
            Every staff member you add gets their own signed training record.
            Assign SOPs, track completion and download certificates any time.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={sendReminders}
            disabled={isDemo}
            data-testid="team-send-reminders-btn"
            title="Email every staff member with outstanding items"
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 hover:border-slate-300 disabled:opacity-40 text-slate-700 hover:text-slate-900 px-3.5 py-2.5 text-sm font-semibold"
          >
            <Send size={14} /> Send reminders
          </button>
          <button
            onClick={() => setShowAdd(true)}
            disabled={isDemo}
            data-testid="team-add-btn"
            className="inline-flex items-center gap-2 rounded-lg bg-[#0B1220] hover:bg-[#131c31] disabled:opacity-40 text-white px-4 py-2.5 text-sm font-semibold"
          >
            <UserPlus size={15} /> Add team member
          </button>
        </div>
      </div>

      {notice && (
        <div data-testid="team-notice" className="mb-5 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-[13px] text-emerald-800">
          {notice}
        </div>
      )}

      {isDemo && (
        <div className="mb-5 rounded-lg border border-[#D4A65A]/30 bg-[#FDF6E7] px-4 py-3 text-[13px] text-[#8C6B33]">
          You are in demo mode — team edits are disabled. Create your own
          workspace to invite real staff.
        </div>
      )}

      {error && (
        <div data-testid="team-error" className="mb-5 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      {/* Filter bar */}
      <div className="mb-5 flex flex-wrap items-center gap-2" data-testid="team-filter-bar">
        {[
          { key: "all", label: "Everyone" },
          { key: "active", label: "Activated" },
          { key: "pending", label: "Invite pending" },
        ].map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            data-testid={`team-filter-${f.key}`}
            className={`text-[12px] font-semibold rounded-full px-3.5 py-1.5 border transition-colors ${
              filter === f.key
                ? "bg-[#0B1220] text-white border-[#0B1220]"
                : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
            }`}
          >
            {f.label} <span className="ml-1 text-[11px] opacity-70">({counts[f.key] || 0})</span>
          </button>
        ))}
        <div className="ml-auto relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="team-search"
            placeholder="Search name, email, title…"
            className="pl-8 pr-3 py-1.5 text-[12.5px] rounded-full border border-slate-200 focus:outline-none focus:border-slate-400 w-60"
          />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="team-list">
        {filtered.length === 0 && (
          <div className="col-span-full">
            <EmptyState
              icon={UserPlus}
              title={members.length === 0 ? "No team members yet" : "No matches for that filter"}
              description={
                members.length === 0
                  ? "Invite your first staff member and their training assignments will start appearing here automatically."
                  : "Try clearing the search or switching the filter above."
              }
              action={
                members.length === 0 && !isDemo ? (
                  <Button
                    variant="primary"
                    icon={UserPlus}
                    onClick={() => setShowAdd(true)}
                    data-testid="team-empty-add-btn"
                  >
                    Add team member
                  </Button>
                ) : null
              }
            />
          </div>
        )}
        {filtered.map((m) => (
          <MemberCard
            key={m.id}
            m={m}
            comp={compByMember[m.id]}
            disabled={isDemo}
            onResend={() => resendInvite(m.id)}
            onCopyLink={() => copyInviteLink(m.id)}
            onEdit={() => setEditing(m)}
            onRemove={() => setRemoving(m)}
          />
        ))}
      </div>

      {showAdd && (
        <AddMemberModal
          onClose={() => setShowAdd(false)}
          onCreated={(res) => {
            setNotice(
              res.invite_sent
                ? `Invite email sent to ${res.email}. They'll set their password and land on their My training dashboard.`
                : `${res.name || "Team member"} has been added, but the invitation email could not be delivered to ${res.email}. Check the address is correct, then use "Resend invite" on their card — or share the invite link directly.`
            );
            setShowAdd(false);
            load();
          }}
        />
      )}

      {editing && (
        <EditMemberModal
          m={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setNotice("Team member updated.");
            setEditing(null);
            load();
          }}
        />
      )}

      {removing && (
        <RemoveMemberConfirm
          m={removing}
          onClose={() => setRemoving(null)}
          onDeleted={() => {
            setNotice(`Removed ${removing.name} from your team.`);
            setRemoving(null);
            load();
          }}
        />
      )}
    </AppShell>
  );
}

/* ---------------- Add ---------------- */

function AddMemberModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", job_title: "", department: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [errorHint, setErrorHint] = useState("");

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    setErrorHint("");
    try {
      const res = await api.post("/team", form);
      onCreated(res);
    } catch (err) {
      // Cross-tenant email conflict: give the owner a clear next step.
      const msg = err.message || "Something went wrong";
      if (/already/i.test(msg) && /email/i.test(msg)) {
        setError(msg);
        setErrorHint(
          "If this person already has a CanniComply account with another workspace, ask them to invite you or use a different work email."
        );
      } else {
        setError(msg);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-start md:items-center justify-center p-4 md:p-5 overflow-y-auto" onClick={() => !busy && onClose()}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 my-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="team-add-modal"
      >
        <div className="flex items-start justify-between">
          <div>
            <h2 className="font-display text-xl font-semibold text-slate-900">Add a team member</h2>
            <p className="text-[13px] text-slate-500 mt-1">They'll receive an email invite to set a password.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <Field
            label="Full name"
            testid="team-input-name"
            value={form.name}
            onChange={(v) => setForm({ ...form, name: v })}
            required
          />
          <Field
            label="Work email"
            type="email"
            testid="team-input-email"
            value={form.email}
            onChange={(v) => setForm({ ...form, email: v })}
            required
          />
          <Field
            label="Job title"
            testid="team-input-job-title"
            value={form.job_title}
            onChange={(v) => setForm({ ...form, job_title: v })}
            placeholder="e.g. Dispensing Pharmacist"
            required
          />
          <Field
            label="Department (optional)"
            testid="team-input-department"
            value={form.department}
            onChange={(v) => setForm({ ...form, department: v })}
            placeholder="e.g. Dispensary, Quality"
          />

          {error && (
            <div data-testid="team-add-error" className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[13px] text-rose-700">
              <div>{error}</div>
              {errorHint && <div className="mt-1 text-[12px] text-rose-800/80">{errorHint}</div>}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            data-testid="team-input-submit"
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#D4A65A] hover:bg-[#E6BE7A] disabled:opacity-40 text-[#0B1220] px-4 py-2.5 text-sm font-semibold"
          >
            {busy ? <><Loader2 size={13} className="animate-spin" /> Inviting…</> : "Send invitation"}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ---------------- Edit ---------------- */

function EditMemberModal({ m, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: m.name || "",
    job_title: m.job_title || m.role || "",
    department: m.department || "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.patch(`/team/${m.id}`, form);
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-start md:items-center justify-center p-4 md:p-5 overflow-y-auto" onClick={() => !busy && onClose()}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 my-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="team-edit-modal"
      >
        <div className="flex items-start justify-between">
          <div>
            <h2 className="font-display text-xl font-semibold text-slate-900">Edit team member</h2>
            <p className="text-[13px] text-slate-500 mt-1 truncate">
              {m.email} — email cannot be changed. Remove and re-invite to change email.
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <Field
            label="Full name"
            testid="team-edit-name"
            value={form.name}
            onChange={(v) => setForm({ ...form, name: v })}
            required
          />
          <Field
            label="Job title"
            testid="team-edit-job-title"
            value={form.job_title}
            onChange={(v) => setForm({ ...form, job_title: v })}
            required
          />
          <Field
            label="Department (optional)"
            testid="team-edit-department"
            value={form.department}
            onChange={(v) => setForm({ ...form, department: v })}
          />
          {error && (
            <div data-testid="team-edit-error" className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[13px] text-rose-700">
              {error}
            </div>
          )}
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="text-[13px] text-slate-600 hover:text-slate-900 px-3 py-2"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              data-testid="team-edit-submit"
              className="inline-flex items-center gap-2 rounded-lg bg-[#0B1220] hover:bg-[#131c31] disabled:opacity-40 text-white px-4 py-2 text-sm font-semibold"
            >
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Pencil size={13} />}
              Save changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ---------------- Remove ---------------- */

function RemoveMemberConfirm({ m, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const canDelete = confirm.trim().toLowerCase() === "remove";

  async function submit() {
    if (!canDelete) return;
    setBusy(true);
    setError("");
    try {
      await api.del(`/team/${m.id}`);
      onDeleted();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-start md:items-center justify-center p-4 md:p-5 overflow-y-auto" onClick={() => !busy && onClose()}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 my-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="team-remove-modal"
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-rose-700 font-semibold">
              <AlertTriangle size={12} /> Remove team member
            </div>
            <h2 className="mt-1 font-display text-xl font-semibold text-slate-900">
              Remove {m.name}?
            </h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X size={18} />
          </button>
        </div>
        <p className="mt-3 text-[13px] text-slate-600 leading-relaxed">
          Their staff account will be revoked and their outstanding assignments
          will be deleted. Certificates they've already earned are retained
          under your business record for audit purposes.
        </p>
        <div className="mt-4">
          <label className="text-[11px] uppercase tracking-[0.16em] text-slate-500">
            Type <span className="font-mono text-slate-700">remove</span> to confirm
          </label>
          <input
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            data-testid="team-remove-confirm-input"
            className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-sm text-slate-900 focus:border-rose-400 focus:ring-2 focus:ring-rose-100 focus:outline-none"
            autoFocus
          />
        </div>
        {error && (
          <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[13px] text-rose-700">
            {error}
          </div>
        )}
        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="text-[13px] text-slate-600 hover:text-slate-900 px-3 py-2"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy || !canDelete}
            data-testid="team-remove-submit"
            className="inline-flex items-center gap-2 rounded-lg bg-rose-600 hover:bg-rose-700 disabled:opacity-40 text-white px-4 py-2 text-sm font-semibold"
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
            Remove
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Field ---------------- */

function Field({ label, testid, value, onChange, type = "text", required, placeholder }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.16em] text-slate-500">{label}</span>
      <input
        type={type}
        required={required}
        data-testid={testid}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 focus:border-[#D4A65A] focus:ring-2 focus:ring-[#D4A65A]/20 focus:outline-none transition-colors"
      />
    </label>
  );
}

/* ---------------- Card ---------------- */

function MemberCard({ m, comp, disabled, onResend, onCopyLink, onEdit, onRemove }) {
  const done = m.assignments_completed;
  const total = m.assignments_total;
  const statusColour =
    m.training_status === "Fully trained"
      ? "text-emerald-700 bg-emerald-50 border-emerald-200"
      : m.training_status === "In progress"
      ? "text-amber-700 bg-amber-50 border-amber-200"
      : "text-slate-600 bg-slate-50 border-slate-200";
  const cAssigned = comp?.assigned || 0;
  const cCompleted = comp?.completed || 0;
  const inviteStatus = m.invite_status;
  const inviteColour =
    inviteStatus === "active"
      ? "text-emerald-700 bg-emerald-50 border-emerald-200"
      : inviteStatus === "pending"
      ? "text-amber-700 bg-amber-50 border-amber-200"
      : "text-slate-600 bg-slate-50 border-slate-200";
  const inviteLabel =
    inviteStatus === "active"
      ? "Activated"
      : inviteStatus === "pending"
      ? "Invite pending"
      : "Not invited";
  return (
    <div
      className="rounded-2xl border border-slate-200 bg-white p-5 flex flex-col"
      data-testid={`team-member-${m.id}`}
    >
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-full bg-[#0B1220] text-[#D4A65A] flex items-center justify-center text-[13px] font-semibold">
          {initials(m.name)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-slate-900 text-[15px] font-semibold tracking-tight truncate">{m.name}</div>
          <div className="text-[12px] text-slate-500 truncate">
            {m.job_title || m.role}
            {m.department ? <span className="text-slate-400"> · {m.department}</span> : null}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onEdit}
            disabled={disabled}
            data-testid={`team-edit-${m.id}`}
            className="p-1.5 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100 disabled:opacity-30 transition-colors"
            title="Edit"
          >
            <Pencil size={13} />
          </button>
          <button
            onClick={onRemove}
            disabled={disabled}
            data-testid={`team-remove-${m.id}`}
            className="p-1.5 rounded-md text-slate-500 hover:text-rose-700 hover:bg-rose-50 disabled:opacity-30 transition-colors"
            title="Remove"
          >
            <Trash2 size={13} />
          </button>
        </div>
      </div>

      <div className="mt-3 text-[12px] text-slate-500 inline-flex items-center gap-1.5">
        <Mail size={11} className="text-slate-400" />
        <span className="truncate">{m.email}</span>
      </div>

      <div className="mt-3 flex items-center justify-between gap-2">
        <span
          data-testid={`team-invite-status-${m.id}`}
          className={`px-2 py-0.5 rounded-full border text-[10px] font-semibold inline-flex items-center gap-1 ${inviteColour}`}
        >
          <ShieldCheck size={10} /> {inviteLabel}
        </span>
        {inviteStatus === "pending" && (
          <div className="flex items-center gap-3">
            <button
              onClick={onCopyLink}
              disabled={disabled}
              data-testid={`team-copy-invite-link-${m.id}`}
              className="text-[11px] font-semibold text-slate-600 hover:text-[#0B1220] disabled:opacity-40 inline-flex items-center gap-1"
              title="Copy invite link to clipboard — share via WhatsApp, SMS, Slack, etc."
            >
              <Link2 size={11} /> Copy link
            </button>
            <button
              onClick={onResend}
              disabled={disabled}
              data-testid={`team-resend-invite-${m.id}`}
              className="text-[11px] font-semibold text-[#8C6B33] hover:text-[#0B1220] disabled:opacity-40 inline-flex items-center gap-1"
            >
              <Send size={11} /> Resend invite
            </button>
          </div>
        )}
      </div>

      <div className="mt-4 pt-4 border-t border-slate-100">
        <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.14em] text-slate-500">
          <span>Training</span>
          <span className={`px-2 py-0.5 rounded-full border text-[10px] font-semibold ${statusColour}`}>
            {m.training_status}
          </span>
        </div>
        <div className="mt-2 flex items-center gap-2 text-[13px] text-slate-700">
          <BadgeCheck size={14} className="text-emerald-500" />
          {done} of {total} completed
        </div>
        {m.latest_completion && (
          <div className="mt-1 text-[11px] text-slate-500">
            Latest completion {new Date(m.latest_completion).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
          </div>
        )}
      </div>

      {cAssigned > 0 && (
        <div
          className="mt-3 pt-3 border-t border-slate-100"
          data-testid={`team-member-competencies-${m.id}`}
        >
          <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.14em] text-slate-500">
            <span>Competencies</span>
            <span className="font-mono tabular-nums text-slate-600">
              {cCompleted}/{cAssigned}
            </span>
          </div>
          <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#D4A65A] to-emerald-400"
              style={{
                width: `${cAssigned ? Math.round((cCompleted / cAssigned) * 100) : 0}%`,
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function initials(name) {
  return (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join("");
}
